import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and CSS display grid for height auto animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on CSS display grid and inert attributes to create smooth height animations
 * when expanding/collapsing content areas.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with CSS grid animations to provide smooth height transitions.
   */
  toggleDisclosure = () => {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const wasExpanded = trigger.getAttribute('aria-expanded') === 'true';
    const next = !wasExpanded;

    // setAttribute (not IDL-only) so Safari invalidates [aria-expanded] / sibling CSS.
    trigger.setAttribute('aria-expanded', String(next));
    trigger.setAttribute(
      'aria-label',
      `${next ? trigger.dataset.disclosureClose : trigger.dataset.disclosureOpen}`
    );
    // toggleAttribute: more reliable for [inert] CSS than content.inert = bool on iOS Safari.
    content.toggleAttribute('inert', !next);

    if (next) {
      // Sticky cart-drawer summary can clip the panel on mobile Safari — unlock + scroll.
      const stickyHost = this.closest('[cart-summary-sticky]');
      if (stickyHost) stickyHost.setAttribute('cart-summary-sticky', 'false');

      requestAnimationFrame(() => {
        const field = content.querySelector('input, textarea');
        field?.focus?.({ preventScroll: true });
        (field || content).scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }
  };
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}
